import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, X, ShieldCheck } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { BottomSheet } from "@/components/revanche/BottomSheet";
import { PrimaryCTA } from "@/components/revanche/PrimaryCTA";
import { startOffer, getPricing, getStripeConfig, checkoutEmbedded, paymentStatus } from "@/lib/pay";
import { isTestMode, testFulfill } from "@/lib/testmode";
import { getSessionId } from "@/lib/api";

const eur = (v) => `${v.toFixed(2).replace(".", ",")} €`;

const INCLUS = ["L'analyse complète", "Le rapport téléchargeable", "Deux analyses par mois", "L'accès au coach Mike"];

// Compte à rebours réel, basé sur l'heure serveur (jamais réinitialisé côté client).
const useCountdown = (pricing) => {
    const [left, setLeft] = useState(null);
    useEffect(() => {
        if (!pricing?.offer_expires_at) { setLeft(0); return undefined; }
        const skew = Date.parse(pricing.server_now) - Date.now();
        const end = Date.parse(pricing.offer_expires_at);
        const tick = () => setLeft(Math.max(0, Math.floor((end - (Date.now() + skew)) / 1000)));
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [pricing?.offer_expires_at, pricing?.server_now]);
    return left;
};

export const OfferSheet = ({ open, onClose, onPaid }) => {
    const [pricing, setPricing] = useState(null);
    const [stage, setStage] = useState("offer"); // offer | checkout | confirming
    const [stripePromise, setStripePromise] = useState(null);
    const [error, setError] = useState("");
    const checkoutId = useRef(null);
    const left = useCountdown(pricing);

    useEffect(() => {
        // On précharge prix, prix barré et compte à rebours DÈS le montage,
        // pour que la fenêtre s'affiche complète d'un coup (jamais de « Chargement… »).
        startOffer().catch(() => {}).finally(() => getPricing().then(setPricing).catch(() => {}));
    }, []);

    // Prix : offre de lancement décidée côté serveur (réglages). Le compte à rebours n'est qu'une urgence.
    const active = pricing ? pricing.offer_active : false;
    const first = pricing ? pricing.monthly_first : null;
    const monthly = pricing ? pricing.monthly : null;
    const mm = String(Math.floor(Math.max(0, left || 0) / 60)).padStart(2, "0");
    const ss = String(Math.max(0, left || 0) % 60).padStart(2, "0");

    const pay = async () => {
        setError("");
        try {
            const cfg = await getStripeConfig();
            setStripePromise(loadStripe(cfg.publishable_key));
            setStage("checkout");
        } catch (e) { setError("Paiement indisponible, réessaie."); }
    };

    const fetchClientSecret = useMemo(() => async () => {
        const d = await checkoutEmbedded("sub_monthly");
        checkoutId.current = d.session_id;
        return d.client_secret;
    }, []);

    const onComplete = async () => {
        setStage("confirming");
        for (let i = 0; i < 30; i += 1) {
            try {
                const s = await paymentStatus(checkoutId.current);
                if (s.payment_status === "paid") { onPaid(); return; }
            } catch (e) { /* on réessaie */ }
            await new Promise((r) => setTimeout(r, 1500));
        }
        setError("Paiement en cours de confirmation. Recharge la page dans un instant.");
    };

    const simulate = async () => {
        try {
            await testFulfill(getSessionId());
            onPaid();
        } catch (e) { setError("Simulation impossible."); }
    };

    const close = () => { if (stage !== "confirming") { setStage("offer"); onClose(); } };

    return (
        <BottomSheet open={open} onClose={close} height={stage === "offer" ? "72dvh" : "92dvh"} testId="offer-sheet" backdropOpacity={stage === "offer" ? 0.35 : 0.72}>
            <button data-testid="offer-close" onClick={close} aria-label="Fermer"
                className="tap-feedback absolute right-3 top-2 z-10 w-9 h-9 flex items-center justify-center rounded-full"
                style={{ color: "var(--text-secondary)", background: "var(--surface-2)" }}>
                <X size={20} />
            </button>
            {stage === "offer" && (
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-none px-6 pt-3 pb-5 flex flex-col">
                    <h2 className="font-body font-bold text-white" style={{ fontSize: 24 }} data-testid="offer-title">Offre de lancement</h2>

                    <div className="flex items-end gap-3 mt-4">
                        {active && (
                            <span className="font-body line-through" style={{ fontSize: 22, color: "var(--text-secondary)", paddingBottom: 6 }} data-testid="offer-price-old">{monthly != null ? eur(monthly) : "—"}</span>
                        )}
                        <span className="font-display" style={{ fontSize: 56, fontWeight: 800, lineHeight: 0.9, color: "var(--red)" }} data-testid="offer-price">
                            {first != null ? eur(first) : "—"}
                        </span>
                    </div>
                    {active && (
                        <p className="font-display mt-3" style={{ fontSize: 28, fontWeight: 800, color: "#FFFFFF", letterSpacing: "0.04em" }} data-testid="offer-timer">
                            {mm}:{ss}
                        </p>
                    )}

                    <div className="flex items-center gap-2 mt-4" data-testid="offer-guarantee">
                        <ShieldCheck size={20} strokeWidth={2.5} style={{ color: "#E10600", flex: "none" }} />
                        <span className="font-body font-bold text-white" style={{ fontSize: 16 }}>Satisfait ou remboursé sous 48h</span>
                    </div>

                    <ul className="mt-5 flex flex-col gap-3" data-testid="offer-includes">
                        {INCLUS.map((t) => (
                            <li key={t} className="flex items-center gap-3 font-body text-white" style={{ fontSize: 16 }}>
                                <Check size={20} strokeWidth={2} style={{ color: "var(--red)", flex: "none" }} />{t}
                            </li>
                        ))}
                    </ul>

                    <div className="mt-auto pt-5">
                        {error && <p className="font-body text-center mb-2" style={{ fontSize: 13, color: "var(--red)" }} data-testid="offer-error">{error}</p>}
                        <PrimaryCTA dataTestId="btn-unlock-pay" onClick={pay} disabled={first == null}>
                            {first != null ? `Débloquer pour ${eur(first)}` : "Chargement…"}
                        </PrimaryCTA>
                        {(isTestMode()) && (
                            <button data-testid="btn-simulate-pay" onClick={simulate}
                                className="tap-feedback w-full mt-2 rounded-full font-body text-sm"
                                style={{ height: 44, border: "1px dashed var(--red)", color: "var(--red)" }}>
                                Simuler le paiement
                            </button>
                        )}
                        <p className="font-body text-center mt-3" style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                            Sans engagement, résiliable en ligne à tout moment.
                        </p>
                        <p className="font-body text-center mt-2" data-testid="offer-legal" style={{ fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.4 }}>
                            En continuant, tu acceptes les <a href="/cgv" target="_blank" rel="noopener" className="underline">CGV</a> et la <a href="/confidentialite" target="_blank" rel="noopener" className="underline">politique de confidentialité</a>.
                        </p>
                    </div>
                </div>
            )}

            {stage !== "offer" && (
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-none px-3 pt-2 pb-4 relative" data-testid="embedded-checkout">
                    {stripePromise && stage === "checkout" && (
                        <div style={{ backgroundColor: "#FFFFFF", borderRadius: "var(--radius-card)", overflow: "hidden", minHeight: 400 }}>
                            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret, onComplete }}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                    {stage === "confirming" && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4" data-testid="payment-confirming">
                            <div className="w-10 h-10 rounded-full border-2 border-ink-700 border-t-revred animate-spin" />
                            <p className="font-body text-white text-base">Confirmation du paiement…</p>
                        </div>
                    )}
                    {error && <p className="font-body text-center mt-3" style={{ fontSize: 13, color: "var(--red)" }}>{error}</p>}
                </div>
            )}
        </BottomSheet>
    );
};
